import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Trash2, Upload } from "lucide-react";
import { Btn, F, inputCls } from "@/pages/portal/PortalSteps";

const L = {
  en: {
    title: "EUDR reference on your export customs declaration",
    intro: "Your mill dispatches from the EU. For each delivery or batch you send us, enter the EUDR reference (and verification number) shown on your export customs declaration. We declare it when the goods re-enter the EU, so no new due diligence statement is needed.",
    reference: "EUDR reference", verification: "Verification number", delivery_note: "Delivery note no.", date: "Dispatch date", material: "Our material no.",
    batch: "Batch / reel", quantity: "Quantity", po_no: "Our PO no. (optional)", add: "Add reference", csv: "Upload CSV",
    csv_hint: "CSV columns: EUDR reference, Verification, Delivery note, Date (YYYY-MM-DD), Material, Batch, Quantity, PO no.",
    none: "No references yet.", saved: "saved", rejected: "rejected", matched: "matched to our PO", unmatched: "not matched yet", del: "Delete",
  },
  fr: {
    title: "Référence EUDR de votre déclaration d'exportation en douane",
    intro: "Votre usine expédie depuis l'UE. Pour chaque livraison ou lot, indiquez la référence EUDR (et le numéro de vérification) figurant sur votre déclaration d'exportation. Nous la déclarons lors de la réimportation dans l'UE ; aucune nouvelle déclaration de diligence raisonnée n'est nécessaire.",
    reference: "Référence EUDR", verification: "Numéro de vérification", delivery_note: "N° de bon de livraison", date: "Date d'expédition", material: "Notre n° d'article",
    batch: "Lot / bobine", quantity: "Quantité", po_no: "Notre n° de commande (facultatif)", add: "Ajouter la référence", csv: "Importer un CSV",
    csv_hint: "Colonnes CSV : EUDR reference, Verification, Delivery note, Date (AAAA-MM-JJ), Material, Batch, Quantity, PO no.",
    none: "Aucune référence pour l'instant.", saved: "enregistrées", rejected: "rejetées", matched: "rapprochée de notre commande", unmatched: "pas encore rapprochée", del: "Supprimer",
  },
};
const FIELDS = ["reference", "verification", "delivery_note", "date", "material", "batch", "quantity", "po_no"];

export default function PortalMillRefs({ base, lang, materials, ro }) {
  const t = L[lang] || L.en;
  const [d, setD] = useState(null);
  const [f, setF] = useState({});
  const [msg, setMsg] = useState("");
  const file = useRef();
  const err = (e) => setMsg(e?.response?.data?.detail || String(e));
  useEffect(() => { axios.get(`${base}/mill-refs`).then((r) => setD(r.data)).catch(err); }, [base]);
  const add = async () => {
    try { const r = await axios.post(`${base}/mill-refs`, { ...f, quantity: f.quantity ? Number(f.quantity) : null }); setD(r.data); setF({}); setMsg(""); } catch (e) { err(e); }
  };
  const upload = async (x) => {
    const fd = new FormData(); fd.append("file", x);
    try { const r = await axios.post(`${base}/mill-refs/upload`, fd); setD(r.data); setMsg(`${r.data.saved} ${t.saved}${r.data.rejected.length ? ` · ${r.data.rejected.length} ${t.rejected}: ${r.data.rejected.slice(0, 3).map((z) => `#${z.row} ${z.reason}`).join("; ")}` : ""}`); } catch (e) { err(e); }
  };
  const del = async (id) => { try { const r = await axios.delete(`${base}/mill-refs/${id}`); setD(r.data); } catch (e) { err(e); } };
  if (!d) return null;
  return (
    <div className="mt-8 rounded-xl border border-sky-200 bg-sky-50/50 p-4" data-testid="portal-mill-refs">
      <h3 className="font-semibold text-slate-900">{t.title}</h3>
      <p className="mt-1 text-sm text-slate-600">{t.intro}</p>
      {!ro && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FIELDS.map((k) => (
              <F key={k} label={t[k]}>
                {k === "material" && materials.length ? (
                  <select data-testid={`mill-ref-${k}`} className={inputCls} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
                    <option value="" />{materials.map((m) => <option key={m.material} value={m.material}>{m.material} {m.description ? `· ${m.description}` : ""}</option>)}
                  </select>
                ) : <input data-testid={`mill-ref-${k}`} type={k === "date" ? "date" : "text"} className={inputCls} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}
              </F>))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Btn data-testid="mill-ref-add" disabled={!f.reference || !f.material} onClick={add}>{t.add}</Btn>
            <Btn variant="outline" data-testid="mill-ref-csv" onClick={() => file.current.click()}><Upload className="h-4 w-4" />{t.csv}</Btn>
            <input ref={file} type="file" accept=".csv,.xlsx" className="hidden" data-testid="mill-ref-csv-input" onChange={(e) => { const x = e.target.files[0]; e.target.value = ""; if (x) upload(x); }} />
            <span className="text-xs text-slate-500">{t.csv_hint}</span>
          </div>
        </>)}
      {msg && <p className="mt-2 text-sm text-slate-700" data-testid="mill-ref-msg">{msg}</p>}
      <div className="mt-4 space-y-1.5" data-testid="mill-ref-list">
        {d.refs.length ? d.refs.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`mill-ref-row-${r.id}`}>
            <span className="font-mono font-semibold">{r.reference}</span>{r.verification && <span className="font-mono text-xs text-slate-500">{r.verification}</span>}
            <span className="text-xs text-slate-600">{[r.delivery_note, r.date, r.material, r.batch, r.quantity].filter(Boolean).join(" · ")}</span>
            <span className={`text-xs ${r.matched ? "text-emerald-700" : "text-amber-700"}`}>{r.matched ? t.matched : t.unmatched}</span>
            {!ro && <button type="button" aria-label={t.del} className="ml-auto text-slate-400 hover:text-rose-600" data-testid={`mill-ref-delete-${r.id}`} onClick={() => del(r.id)}><Trash2 className="h-4 w-4" /></button>}
          </div>)) : <p className="text-sm text-slate-500">{t.none}</p>}
      </div>
    </div>
  );
}
